import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { DOB_FORMAT, formatDobInput, formatDobText, dobTextToISO, getDobError } from '../../utils/helpers';

const IDLE_BORDER = 'border-teamcare-blue focus-within:border-teamcare-blue focus-within:ring-teamcare-blue';
const ERROR_BORDER = 'border-error focus-within:border-error focus-within:ring-error';

interface DobFieldProps {
  value?: string;
  onChange: (iso: string, isValid: boolean) => void;
  icon?: LucideIcon;
  borderClassName?: string;
}

// Typed-only date-of-birth field (MM/DD/YYYY mask, no calendar) with its own error display.
// Talks ISO to the parent; keeps the masked text and the touched flag to itself.
export const DobField = ({ value = '', onChange, icon: Icon, borderClassName = IDLE_BORDER }: DobFieldProps) => {
  const [text, setText] = useState(() => formatDobText(value));
  const [touched, setTouched] = useState(false);
  const error = touched ? getDobError(text) : '';

  // The parent replaced the ISO value from outside (not through typing here), e.g. a form reset.
  useEffect(() => {
    setText((current) => (dobTextToISO(current) === value ? current : formatDobText(value)));
  }, [value]);

  const handleChange = (typed: string) => {
    const next = formatDobInput(typed, typed.length < text.length);
    setText(next);
    onChange(dobTextToISO(next), !getDobError(next));
  };

  return (
    <>
      <label className={`input input-bordered flex items-center gap-2 bg-white text-black ${error ? ERROR_BORDER : borderClassName}`}>
        {Icon && <Icon size={18} className="text-gray-400" />}
        <input
          type="text"
          inputMode="numeric"
          maxLength={DOB_FORMAT.length}
          placeholder={DOB_FORMAT}
          autoComplete="off"
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => setTouched(true)}
          className="grow bg-transparent text-black placeholder-gray-400 outline-none"
        />
      </label>
      {error && (
        <label className="label">
          <span className="label-text-alt text-error">{error}</span>
        </label>
      )}
    </>
  );
};
